import { GridPaginationModel, GridSortModel } from "@mui/x-data-grid";
import { useCallback, useMemo, useState } from "react";
import { useSearchParams } from "react-router";

const getParamsFromUrl = (searchParams: URLSearchParams): Record<string, string> => {
    return Object.fromEntries(searchParams.entries());
};

type SearchParamsImpl = () => [URLSearchParams, ReturnType<typeof useSearchParams>[1]];

const useQueryStringGridModels = <GqlParams>(
    convert: (plain: Record<string, string>) => GqlParams,
    defaults?: Partial<GqlParams>,
    searchParamsImpl?: SearchParamsImpl,
) => {
    // biome-ignore lint/correctness/useHookAtTopLevel: assume `searchParamsImpl` is stable
    const [searchParams, setSearchParams] = searchParamsImpl !== undefined ? searchParamsImpl() : useSearchParams();

    // This is our reliable "single source of truth". It's initialized once from the URL.
    const [localParams, setLocalParams] = useState<Record<string, string>>(() => {
        const fromSearchParams = getParamsFromUrl(searchParams);
        return { ...defaults, ...fromSearchParams };
    });

    // This is now the ONLY function that changes state. It updates both the URL and our local state.
    const updateStateAndUrl = useCallback(
        (newParams: Record<string, string | null | undefined>) => {
            const nextEntries = Object.entries({ ...localParams, ...newParams }).filter(([_, value]) => {
                return value !== null && value !== undefined;
            }) as [string, string][];

            const nextParams: Record<string, string> = Object.fromEntries(nextEntries);

            setLocalParams(nextParams);
            setSearchParams(nextEntries);
        },
        [setSearchParams, localParams],
    );

    const onPaginationModelChange = useCallback(
        (model: GridPaginationModel) => {
            updateStateAndUrl({
                page: model.page.toString(),
                pageSize: model.pageSize.toString(),
            });
        },
        [updateStateAndUrl],
    );

    const onSortModelChange = useCallback(
        (model: GridSortModel) => {
            updateStateAndUrl({
                sortBy: model[0]?.field || null,
                sortOrder: model[0]?.sort || null,
            });
        },
        [updateStateAndUrl],
    );

    const onFilterModelChange = useCallback(
        (key: string, value: string | null) => {
            updateStateAndUrl({ [key]: value, page: "0" });
        },
        [updateStateAndUrl],
    );

    const { sortBy, sortOrder, page, pageSize, ...filters } = localParams;

    const paginationModel = useMemo<GridPaginationModel>(() => {
        // First check URL params
        const urlPage = page ? parseInt(page, 10) : undefined;
        const urlPageSize = pageSize ? parseInt(pageSize, 10) : undefined;

        let defaultPage = 0;
        let defaultPageSize = 100;

        // Then check for given defaults
        if (defaults) {
            if ("page" in defaults && typeof defaults.page === "number") {
                defaultPage = defaults.page;
            }
            if ("pageSize" in defaults && typeof defaults.pageSize === "number") {
                defaultPageSize = defaults.pageSize;
            }
        }

        return {
            // Finally use a fallback value
            page: urlPage ?? defaultPage,
            pageSize: urlPageSize ?? defaultPageSize,
        };
    }, [page, pageSize, defaults]);

    const sortModel = useMemo<GridSortModel>(() => {
        let defaultSortBy: string | undefined;
        let defaultSortOrder: string | undefined;

        if (defaults) {
            if ("sortBy" in defaults && typeof defaults.sortBy === "string") {
                defaultSortBy = defaults.sortBy;
            }
            if ("sortOrder" in defaults && typeof defaults.sortOrder === "string") {
                defaultSortOrder = defaults.sortOrder;
            }
        }

        const effectiveSortBy = sortBy || defaultSortBy;
        const effectiveSortOrder = sortOrder || defaultSortOrder;

        return effectiveSortBy
            ? [
                  {
                      field: effectiveSortBy,
                      sort: effectiveSortOrder === "asc" ? "asc" : "desc",
                  },
              ]
            : [];
    }, [sortBy, sortOrder, defaults]);

    const gqlVariables = useMemo(() => {
        const converted = convert(localParams);
        return Object.freeze({ params: converted });
    }, [localParams, convert]);

    const gridModels = useMemo(
        () => ({
            paginationModel,
            sortModel,
            onPaginationModelChange,
            onSortModelChange,
        }),
        [paginationModel, sortModel, onPaginationModelChange, onSortModelChange],
    );

    return useMemo(
        () => ({
            filterModel: filters,
            onFilterModelChange,
            gridModels,
            gqlVariables,
        }),
        // biome-ignore lint/correctness/useExhaustiveDependencies: keep for now
        [filters, onFilterModelChange, gridModels, gqlVariables],
    );
};

export default useQueryStringGridModels;
